import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../../../config';

import {
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  Clock,
  Code,
  GitBranch,
  Lightning,
  Play,
  X,
  XCircle
} from '@phosphor-icons/react';
/**
 * NodeTestModal - Modal for testing individual nodes in isolation
 * 
 * Features:
 * - Test a single node with custom input
 * - Option to run with predecessor outputs
 * - View execution results and timing
 */
const NodeTestModal = ({ 
  isOpen, 
  onClose, 
  node, 
  agentId,
  flow,
  orgId
}) => {
  const [inputData, setInputData] = useState('{\n  "text": "Sample input for testing"\n}');
  const [usePredecessors, setUsePredecessors] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Reset state when node changes
  useEffect(() => {
    if (node) {
      setInputData('{\n  "text": "Sample input for testing"\n}');
      setUsePredecessors(false);
      setResult(null);
      setError(null);
      setShowAdvanced(false);
    }
  }, [node?.id]);

  const handleTest = useCallback(async () => {
    if (!node) return;
    
    setIsRunning(true);
    setResult(null);
    setError(null);

    try {
      // Parse input data
      let parsedInput;
      try {
        parsedInput = JSON.parse(inputData);
      } catch (e) {
        // If not valid JSON, wrap as text
        parsedInput = { text: inputData };
      }

      const requestBody = {
        node_id: node.id,
        input_data: parsedInput,
        use_previous_outputs: usePredecessors
      };

      // Include either agent_id or flow
      if (agentId) {
        requestBody.agent_id = agentId;
      } else if (flow) {
        requestBody.flow = flow;
      }

      const response = await fetch(`${BACKEND_URL}/api/flow/test-node`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(orgId && { 'X-Organization-Id': orgId })
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResult(data);
      } else {
        setError(data.detail || data.error || 'Node test failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to test node');
    } finally {
      setIsRunning(false);
    }
  }, [node, inputData, usePredecessors, agentId, flow, orgId]);

  if (!isOpen || !node) return null;

  const nodeType = node.data?.nodeType || node.type || 'unknown';
  const nodeLabel = node.data?.label || nodeType;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-2xl mx-4 bg-[#1a1b23] border border-white/10 rounded-xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-500/20">
                <Lightning size={18} className="text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Test Node</h2>
                <p className="text-sm text-slate-400">{nodeLabel}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Node Info */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-sm">
              <Code size={14} className="text-slate-400" />
              <span className="text-slate-400">Node ID:</span>
              <code className="text-indigo-400">{node.id}</code>
              <span className="text-slate-500 mx-2">|</span>
              <span className="text-slate-400">Type:</span>
              <code className="text-purple-400">{nodeType}</code>
            </div>

            {/* Input Data */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Input Data (JSON)
              </label>
              <textarea
                value={inputData}
                onChange={(e) => setInputData(e.target.value)}
                className="w-full h-32 px-3 py-2 bg-[#0d0e12] border border-white/10 rounded-lg 
                  text-white font-mono text-sm resize-none focus:outline-none focus:ring-2 
                  focus:ring-indigo-500/50 focus:border-indigo-500/50"
                placeholder='{"text": "Your test input here"}'
                spellCheck={false}
              />
            </div>

            {/* Advanced Options */}
            <div>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
              >
                {showAdvanced ? <CaretDown size={16} /> : <CaretRight size={16} />}
                Advanced Options
              </button>
              
              {showAdvanced && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-3 p-3 rounded-lg bg-white/5 space-y-3"
                >
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={usePredecessors}
                      onChange={(e) => setUsePredecessors(e.target.checked)}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-indigo-500 
                        focus:ring-indigo-500/50 focus:ring-offset-0"
                    />
                    <div>
                      <div className="flex items-center gap-2 text-sm text-white">
                        <GitBranch size={14} className="text-amber-400" />
                        Run with predecessor outputs
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Execute all upstream nodes first and use their outputs
                      </p>
                    </div>
                  </label>
                </motion.div>
              )}
            </div>

            {/* Results Section */}
            {(result || error) && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                  {result ? (
                    <>
                      <CheckCircle size={16} className="text-emerald-400" />
                      Test Result
                    </>
                  ) : (
                    <>
                      <XCircle size={16} className="text-red-400" />
                      Error
                    </>
                  )}
                </h3>
                
                {error && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                {result && (
                  <div className="space-y-3">
                    {/* Execution Time */}
                    {result.execution_time_ms && (
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Clock size={14} />
                        Execution time: <span className="text-white">{result.execution_time_ms}ms</span>
                      </div>
                    )}

                    {/* Output */}
                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <p className="text-xs text-emerald-400 mb-2 font-medium">Output:</p>
                      <pre className="text-sm text-white font-mono whitespace-pre-wrap overflow-x-auto max-h-48 overflow-y-auto">
                        {typeof result.output === 'object' 
                          ? JSON.stringify(result.output, null, 2)
                          : String(result.output)
                        }
                      </pre>
                    </div>

                    {/* Predecessor Results (if any) */}
                    {result.predecessor_results && Object.keys(result.predecessor_results).length > 0 && (
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-xs text-amber-400 mb-2 font-medium">Predecessor Outputs:</p>
                        <pre className="text-sm text-white font-mono whitespace-pre-wrap overflow-x-auto max-h-32 overflow-y-auto">
                          {JSON.stringify(result.predecessor_results, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-white/10 bg-white/[0.02]">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleTest}
              disabled={isRunning}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 
                disabled:bg-indigo-500/50 text-white rounded-lg text-sm font-medium 
                transition-colors disabled:cursor-not-allowed"
            >
              {isRunning ? (
                <>
                  <CircleNotch size={16} className="animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Play size={16} />
                  Run Test
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default NodeTestModal;
